import styled from '@emotion/styled';
import {
  Card,
  CardActions as _CardActions,
  CardContent,
  FormControl,
  Input,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { createElement, FC, memo } from 'react';
import { FormattedMessage } from 'react-intl';

import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import mq from '../../../styleUtility/mediaquery';
import { InputtingCreativeFormat } from '../../../types/InputtingCreativeFormat';
import _CreativeFormatCreativeTypeIcon from '../CreativeFormatCreativeTypeIcon';
import _CreativeFormatDevice from '../CreativeFormatDevice';
import CreativeFormatNetworkIcon from '../CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from '../CreativeFormatPlacementLabel';

type Props = {
  isDisabled?: boolean;
  creativeFormatIndex: number;
  creativeFormat: InputtingCreativeFormat;
  originalCreativeFormatPlaybackTime?: number;
  onChangePlaybackTime: ({ index, secondsList }: { index: number; secondsList: Array<number> }) => void;
  className?: string;
};

const InputtingCreativeFormatItem: FC<Props> = ({
  isDisabled,
  creativeFormatIndex,
  creativeFormat,
  originalCreativeFormatPlaybackTime,
  onChangePlaybackTime,
  className,
}) => {
  const selectedPlaybackTimes = originalCreativeFormatPlaybackTime
    ? originalCreativeFormatPlaybackTime
    : creativeFormat.selectedPlaybackTimes.map((playbackTime) => playbackTime.seconds)[0];

  return (
    <Root className={className} variant="elevation" selected={!!creativeFormat.selected}>
      <CardContent>
        <CreativeFormatCreativeTypeIcon creativeType={creativeFormat.creativeType} />

        <Device>
          <CreativeFormatDevice
            network={creativeFormat.network}
            aspectRatio={creativeFormat.aspectRatioItem ? creativeFormat.aspectRatioItem.aspectRatio : null}
          />
        </Device>

        <NetworkAndPlacement>
          <NetworkIcon network={creativeFormat.network} size={16} />
          <PlacementWrap>
            <Typography variant="body2">
              <CreativeFormatPlacementLabel placement={creativeFormat.placement} />
            </Typography>
          </PlacementWrap>
        </NetworkAndPlacement>

        {creativeFormat.aspectRatioItem &&
        creativeFormat.aspectRatioItem.aspectRatio.width * creativeFormat.aspectRatioItem.aspectRatio.height > 0 ? (
          <Typography variant="body2">
            {creativeFormat.aspectRatioItem.aspectRatio.width}:{creativeFormat.aspectRatioItem.aspectRatio.height}（
            {creativeFormat.aspectRatioItem.presetSize.width}:{creativeFormat.aspectRatioItem.presetSize.height}）
          </Typography>
        ) : (
          <Typography variant="body2">
            <FormattedMessage id="creative_format.aspect_ratio.nullable" />
          </Typography>
        )}
      </CardContent>

      {creativeFormat.playbackTime && creativeFormat.playbackTime.availables.length > 0 ? (
        <CardActions>
          <FormControl>
            <Select
              disabled={isDisabled}
              input={<Input name="select-playback-time" />}
              value={selectedPlaybackTimes}
              onChange={(e: any) => {
                e.stopPropagation();
                onChangePlaybackTime({
                  index: creativeFormatIndex,
                  secondsList: [Number(e.target.value)],
                });
              }}
            >
              {creativeFormat.playbackTime.availables.map((item) => {
                return (
                  <MenuItem key={`${creativeFormatIndex}-${item.seconds}`} value={item.seconds}>
                    <FormattedMessage id="creative_format.playback_time" values={{ seconds: item.seconds }} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </CardActions>
      ) : null}
    </Root>
  );
};

const Root = styled(Card)<{ selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  min-height: 235px;
  text-align: center;
  border: ${({ selected }) => (selected ? `2px solid ${theme.palette.primary.main}` : 'default')};

  ${mq('sp')} {
    min-height: 308px;
  }
`;

const PlacementWrap = styled.div`
  max-width: 150px;
  word-break: break-all;

  ${mq('sp')} {
    width: 60px;
  }
`;

const CreativeFormatCreativeTypeIcon = styled(_CreativeFormatCreativeTypeIcon)`
  position: absolute;
  top: ${SPACING_UNIT}px;
  left: ${SPACING_UNIT}px;
`;

const Device = styled.div`
  height: 120px;
  padding-bottom: ${SPACING_REGULAR}px;
`;

const CreativeFormatDevice = styled(_CreativeFormatDevice)`
  height: 100%;
`;

const NetworkAndPlacement = styled('div')`
  ${flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'center',
  })}
`;

const NetworkIcon = styled(CreativeFormatNetworkIcon)`
  margin-right: ${SPACING_UNIT}px;
`;

const CardActions = styled(_CardActions)`
  margin-top: -${SPACING_REGULAR}px;
  justify-content: center;

  ${mq('sp')} {
    margin-top: 8px;
  }
`;

export default memo(InputtingCreativeFormatItem);
